import * as React from 'react';
import { translationForMts, DateFormat, TimeFormat, numberFormatter, currencyFormatter, dateFormatter, pointsFormatter, ITranslationForMts } from 'easit--localization';
import { EvalState, TicketState, IMoneyWithCurrency } from 'eTip--common';

export interface ITicketSummaryPropsBase {
    dateCreated: Date;
    possiblePayout: number;
    finalWin: number;
    winPaid: number;
    finalRate: number;
    ticketTypeAsText: string;
    stake: number;
    stakePoints: number | null;
    handlingFee: number | null;
    handlingFeePoints: number | null;
    ticketState: TicketState,
    evalState: EvalState,
    number?: string,
    productFee: IMoneyWithCurrency | null,
    productName?: string;
    oddsRateAdvantage: number | null,
    rateAdvantageFactor: number | null,
    customerNickName?: string;
    prizePaidDate?: Date;

    showHeader?: boolean;
    className?: string;

    dateCancelationLimit: Date | null;
    canShowCancelButton: boolean;
}

export interface ITicketSummaryProps extends ITicketSummaryPropsBase {
    offerTypeAsText: string;
}

export const ticketSummaryHelper = (() => {
    const getStateText = (ticketState: TicketState, evalState: EvalState, t: ITranslationForMts): string => {
        switch (ticketState) {
            case TicketState.Created:
            case TicketState.Paid: return t.ticket_TicketStateCommonAccepting;
            case TicketState.Accepted: return t.ticket_TicketStateCommonAccepted;
            case TicketState.Settled: return t.ticket_TicketStateSettled;
            case TicketState.Evaluated: return getEvalText(evalState, t);
            case TicketState.Storno: return t.ticket_TicketStateStorno;
            case TicketState.StornoBySystem: return t.ticket_TicketStateStornoBySystem;
            case TicketState.StornoByCustomer: return t.ticket_TicketStateStorno;
            case TicketState.StornoByMts: return t.ticket_TicketStateStornoByMts;
            case TicketState.None: return "?";
        }
    };

    const getEvalText = (evalState: EvalState, t: ITranslationForMts): string => {
        switch (evalState) {
            case EvalState.Win: return t.ticket_EvalTypeWin;
            case EvalState.Loss: return t.ticket_EvalTypeLost;
            case EvalState.Cancel: return t.ticket_EvalTypeCancelled;
            case EvalState.CancelWithHandlingFee: return t.ticket_EvalTypeCancelledWithMP;
            case EvalState.Cashout: return t.ticket_EvalTypeCashout;
            case EvalState.None: return "?";
        }
    }
    return {
        getStateText,
        getEvalText
    }
})();

export const TicketSummary = (props: ITicketSummaryProps) => {
    const t = translationForMts();

    return (
        <div className={`bet-detail-summary ${props.className}`}>
            {
                props.showHeader &&
                <div className="summary-header"></div>
            }

            {
                props.number &&
                <div className="summary-row">
                    <span>{t.betList_TicketNumber}</span>
                    <span className="summary-value">{props.number}</span>
                </div>
            }
            <div className="summary-row">
                <span>{t.bet_TicketStateTitle}</span>
                <span className="summary-value">{ticketSummaryHelper.getStateText(props.ticketState, props.evalState, t)}</span>
            </div>
            <div className="summary-row">
                <span>{t.bet_TicketDatePaid}</span>
                <span className="summary-value">{dateFormatter.formatDateTime(props.dateCreated, DateFormat.DDMMYYYY, TimeFormat.HHMMSS)}</span>
            </div>
            <div className="summary-row">
                <span>{t.bet_Stake}</span>
                <span className="summary-value">{currencyFormatter.format(props.stake)}{props.stakePoints !== null && <><br />{pointsFormatter.format(props.stakePoints)}</>}</span>
            </div>
            <div className="summary-row">
                <span>{t.bet_TotalRate}</span>
                <span className="summary-value">{numberFormatter.format(props.finalRate)}</span>
            </div>
            <div className="summary-row">
                <span>{t.bet_PotentialPrice}</span>
                <span className="summary-value">{currencyFormatter.format(props.possiblePayout)}</span>
            </div>
            {
                !!props.finalWin &&
                <div className="summary-row">
                    <span>{t.bet_FinalWin}</span>
                    <span className="summary-value">{currencyFormatter.format(props.finalWin)}</span>
                </div>
            }
            {
                !!props.winPaid &&
                <div className="summary-row">
                    <span>{t.bet_ToPayout}</span>
                    <span className="summary-value">{currencyFormatter.format(props.winPaid)}</span>
                </div>
            }
            <div className="summary-row">
                <span>{t.bet_TicketType}</span>
                <span className="summary-value">{props.ticketTypeAsText}</span>
            </div>
            <div className="summary-row">
                <span>{t.bet_BetType}</span>
                <span className="summary-value">{props.offerTypeAsText}</span>
            </div>
            {(props.handlingFee || props.handlingFeePoints) &&
                <div className="summary-row">
                    <span>{t.bet_HandlingFee}</span>
                    <span className="summary-value">{currencyFormatter.format(props.handlingFee)}{props.handlingFeePoints && <br />}{props.handlingFeePoints && pointsFormatter.format(props.handlingFeePoints)}</span>
                </div>
            }
            {!!props.productName &&
                <div className="summary-row">
                    <span>{t.bet_ProductName}</span>
                    <span className="summary-value">{props.productName}</span>
                </div>
            }
            {!!props.productFee &&
                <div className="summary-row">
                    <span>{t.bet_ProductFee}</span>
                    <span className="summary-value">{props.productFee.amount} {props.productFee.currency}</span>
                </div>
            }
            {!!props.oddsRateAdvantage &&
                <div className="summary-row">
                    <span>{t.bet_OddsRateAdvantage}</span>
                    <span className="summary-value">{numberFormatter.format(props.oddsRateAdvantage)}</span>
                </div>
            }
            {!!props.rateAdvantageFactor &&
                <div className="summary-row">
                    <span>{t.bet_RateAdvantageFactor}</span>
                    <span className="summary-value">{numberFormatter.format(props.rateAdvantageFactor)}</span>
                </div>
            }
            {!!props.customerNickName &&
                <div className="summary-row">
                    <span>Hráč</span>
                    <span className="summary-value">{props.customerNickName}</span>
                </div>
            }
            {/*!!props.prizePaidDate &&
                <div className="summary-row">
                    <span>Dátum a čas vyplatenia tiketu</span>
                    <span className="summary-value">{dateFormatter.formatDateTime(props.prizePaidDate, DateFormat.DDMMYYYY, TimeFormat.HHMMSS)}</span>
                </div>
            */}
            {!!props.dateCancelationLimit &&
                <div className="summary-row">
                    <span>{t.bet_CancelUntil}</span>
                    <span className="summary-value">{dateFormatter.formatDateTime(props.dateCancelationLimit, DateFormat.DDMMYYYY, TimeFormat.HHMMSS)}</span>
                </div>
            }
        </div>
    )
}